import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, BlockStack, Box, Spinner, Banner, Button, Text, InlineStack, TextField, Modal, Tooltip } from '@shopify/polaris';
import { MagicIcon } from '@shopify/polaris-icons';
import { getRequest } from '../../../../shared/components/functions.jsx';
import { ProjectQuotationToSale } from '../../../../shared/components/transformers.js';
import VersionManager from './VersionManager.jsx';
import QuotationLineTable from './QuotationLineTable.jsx';
import AddProductModal from './AddProductModal.jsx';
import TotalsDashboard from './TotalsDashboard.jsx';
import PdfExportControls from './PdfExportControls.jsx';
import QuotationDiffModal from './QuotationDiffModal.jsx';
import TechDescPanel from './TechDescPanel.jsx';
import AcceptanceBanner from './AcceptanceBanner.jsx';
import LanguageRevisionModal from './LanguageRevisionModal.jsx';
import TextToQuotationModal from './TextToQuotationModal.jsx';
import EventTimeLine from '../../../../shared/components/events/eventTimeLine.jsx';

export default function QuotationBuilderTab({ projectId, project }) {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linesLoading, setLinesLoading] = useState(false);
  const [quotationDetailsLoading, setQuotationDetailsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPreset, setAddPreset] = useState({ area: null, line: null, unit: null });
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [compareState, setCompareState] = useState({ open: false, idA: null, idB: null });
  const [contentTab, setContentTab] = useState(0);
  const [acceptanceData, setAcceptanceData] = useState(null);
  const [showCustomerInteractions, setShowCustomerInteractions] = useState(false);
  const [showLanguageRevision, setShowLanguageRevision] = useState(false);
  const [showTextImport, setShowTextImport] = useState(false);

  const selectedQuotationIsCurrent = selectedQuotation && Number(selectedQuotation.ID) === Number(selectedId);
  const isLocked = !selectedQuotationIsCurrent || selectedQuotation.Status !== 0;
  const canEditSelectedQuotation = selectedQuotationIsCurrent && selectedQuotation.Status === 0;
  const isAccepted = selectedQuotation?.Status === 2;

  // Fetch acceptance data when quotation is accepted
  useEffect(() => {
    if (isAccepted && selectedId) {
      getRequest(`/api/project-quotations/${selectedId}/acceptance`).then((data) => {
        setAcceptanceData(data);
      });
    } else {
      setAcceptanceData(null);
    }
  }, [isAccepted, selectedId]);

  // Load all quotation versions for this project
  const loadQuotations = useCallback(
    async (selectId) => {
      setLoading(true);
      const data = await getRequest(`/api/project-quotations/project/${projectId}`);
      if (data) {
        setQuotations(data);
        const requestedId = selectId != null ? Number(selectId) : null;
        const hasRequestedId = requestedId != null && data.some((quotation) => Number(quotation.ID) === requestedId);
        if (hasRequestedId) {
          setSelectedId(requestedId);
        } else if (data.length > 0) {
          setSelectedId(data[data.length - 1].ID);
        } else {
          setSelectedId(null);
        }
      }
      setLoading(false);
    },
    [projectId]
  );

  // Load lines for selected quotation
  const loadLines = useCallback(async () => {
    if (!selectedId) {
      setLines([]);
      return;
    }
    setLinesLoading(true);
    const data = await getRequest(`/api/project-quotations/${selectedId}/lines`);
    if (data) setLines(data);
    setLinesLoading(false);
    setRefreshKey((k) => k + 1);
  }, [selectedId]);

  // Load selected quotation details
  const loadQuotationDetails = useCallback(async () => {
    if (!selectedId) {
      setSelectedQuotation(null);
      setQuotationDetailsLoading(false);
      return;
    }
    setQuotationDetailsLoading(true);
    try {
      const data = await getRequest(`/api/project-quotations/${selectedId}?_ts=${Date.now()}`);
      if (data) setSelectedQuotation(data);
    } finally {
      setQuotationDetailsLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    const selectedIdStorageKey = `quotationBuilder:${projectId}:selectedQuotationId`;
    const contentTabStorageKey = `quotationBuilder:${projectId}:contentTab`;

    let storedSelectedId = null;
    let storedContentTab = 0;

    if (typeof window !== 'undefined') {
      const selectedIdRaw = window.localStorage.getItem(selectedIdStorageKey);
      const selectedIdParsed = Number.parseInt(String(selectedIdRaw || ''), 10);
      if (Number.isInteger(selectedIdParsed) && selectedIdParsed > 0) {
        storedSelectedId = selectedIdParsed;
      }

      const contentTabRaw = window.localStorage.getItem(contentTabStorageKey);
      const contentTabParsed = Number.parseInt(String(contentTabRaw || ''), 10);
      if (Number.isInteger(contentTabParsed) && contentTabParsed >= 0 && contentTabParsed <= 3) {
        storedContentTab = contentTabParsed;
      }
    }

    setContentTab(storedContentTab);
    loadQuotations(storedSelectedId || undefined);
  }, [projectId, loadQuotations]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const selectedIdStorageKey = `quotationBuilder:${projectId}:selectedQuotationId`;
    if (selectedId) {
      window.localStorage.setItem(selectedIdStorageKey, String(selectedId));
    } else {
      window.localStorage.removeItem(selectedIdStorageKey);
    }
  }, [projectId, selectedId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const contentTabStorageKey = `quotationBuilder:${projectId}:contentTab`;
    window.localStorage.setItem(contentTabStorageKey, String(contentTab));
  }, [projectId, contentTab]);

  useEffect(() => {
    loadLines();
    loadQuotationDetails();
  }, [selectedId, loadLines, loadQuotationDetails]);

  const handleRefresh = (newSelectedId) => {
    loadQuotations(newSelectedId);
  };

  const handleLinesRefresh = () => {
    loadLines();
    loadQuotationDetails();
    // Also refresh the quotation list to update totals in version tabs
    loadQuotations(selectedId);
  };

  // Inline cell edits patch lines locally (no spinner, focus stays intact) and
  // refresh totals quietly once typing settles
  const quietRefreshTimer = useRef(null);
  useEffect(() => () => clearTimeout(quietRefreshTimer.current), []);

  const scheduleQuietRefresh = useCallback(() => {
    clearTimeout(quietRefreshTimer.current);
    quietRefreshTimer.current = setTimeout(async () => {
      const data = await getRequest(`/api/project-quotations/project/${projectId}`);
      if (data) setQuotations(data);
      loadQuotationDetails();
      setRefreshKey((k) => k + 1);
    }, 800);
  }, [projectId, loadQuotationDetails]);

  const handleLineUpdate = useCallback(
    (patch) => {
      setLines((prev) => (prev.some((l) => l.ID === patch.ID) ? prev.map((l) => (l.ID === patch.ID ? { ...l, ...patch } : l)) : [...prev, patch]));
      scheduleQuietRefresh();
    },
    [scheduleQuietRefresh]
  );

  const handleLineRemove = useCallback(
    (lineId) => {
      setLines((prev) => prev.filter((l) => l.ID !== lineId));
      scheduleQuietRefresh();
    },
    [scheduleQuietRefresh]
  );

  // No full reload after a language revision or a text import: the spinner would unmount the open modal (and restart it on remount)
  const handleRevisionApplied = useCallback(() => {
    loadLines();
    loadQuotationDetails();
    scheduleQuietRefresh();
  }, [loadLines, loadQuotationDetails, scheduleQuietRefresh]);

  const handleCompare = (idA, idB) => setCompareState({ open: true, idA, idB });

  const handleConvertToSale = async (quotationId) => {
    const q = await getRequest(`/api/project-quotations/${quotationId}`);
    if (!q) return;
    const qLines = await getRequest(`/api/project-quotations/${quotationId}/lines`);
    const acceptance = await getRequest(`/api/project-quotations/${quotationId}/acceptance`);
    await ProjectQuotationToSale(q, qLines || [], acceptance, project, navigate);
  };

  const handleProductAdded = (data) => {
    if (replaceTarget && data?.ID) {
      handleLineUpdate(data);
      setReplaceTarget(null);
      return;
    }
    handleLinesRefresh();
  };

  const handleAddToGroup = (area, line, unit) => {
    setAddPreset({ area, line, unit });
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <Box padding="400">
        <Spinner size="large" />
      </Box>
    );
  }

  return (
    <Box width="100%">
      <BlockStack gap="400">
        {/* Version tabs + Content */}
        <Card>
          <VersionManager
            projectId={projectId}
            quotations={quotations}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onRefresh={handleRefresh}
            onCompare={handleCompare}
            onConvertToSale={handleConvertToSale}
            onOpenCustomerInteractions={() => setShowCustomerInteractions(true)}
          />

          {selectedId ? (
            <>
              {isAccepted && <AcceptanceBanner acceptanceData={acceptanceData} />}

              {/* Content sub-tabs */}
              <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e1e3e5', marginTop: '16px' }}>
                {[
                  { id: 'pricing', label: 'Pricing' },
                  { id: 'tech-desc', label: 'Technical Descriptions' },
                  { id: 'pdf', label: 'PDF' },
                  { id: 'finance-notes', label: 'Financial & Notes' }
                ].map((tab, idx) => (
                  <div
                    key={tab.id}
                    onClick={() => setContentTab(idx)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: contentTab === idx ? '2px solid #000' : '2px solid transparent',
                      marginBottom: '-1px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: contentTab === idx ? '#f6f6f7' : 'transparent',
                      borderRadius: '6px 6px 0 0',
                      transition: 'background-color 0.15s ease'
                    }}
                  >
                    <Text variant="bodySm" as="span" fontWeight={contentTab === idx ? 'semibold' : 'regular'}>
                      {tab.label}
                    </Text>
                  </div>
                ))}
                <div style={{ marginLeft: 'auto', paddingBottom: '4px' }}>
                  <Tooltip content={canEditSelectedQuotation ? 'Proofread or translate every text in this quotation' : 'Only draft quotations can be revised'}>
                    <Button icon={MagicIcon} size="slim" onClick={() => setShowLanguageRevision(true)} disabled={!canEditSelectedQuotation}>
                      Revise language
                    </Button>
                  </Tooltip>
                </div>
              </div>

              {/* Pricing Tab */}
              {contentTab === 0 && (
                <div style={{ minHeight: '400px', marginTop: '16px' }}>
                  <BlockStack gap="300">
                    <InlineStack align="space-between">
                      <Text variant="headingSm" as="h3">
                        {`All Items (${lines.length})`}
                      </Text>
                      <InlineStack gap="200">
                        <Tooltip content={canEditSelectedQuotation ? 'Paste a customer request, an email or notes and let AI write the quotation' : 'Only draft quotations can be edited'}>
                          <Button icon={MagicIcon} onClick={() => setShowTextImport(true)} disabled={!canEditSelectedQuotation}>
                            Text to quotation
                          </Button>
                        </Tooltip>
                        <Button variant="primary" onClick={() => handleAddToGroup(null, null, null)} disabled={!canEditSelectedQuotation} loading={quotationDetailsLoading}>
                          + Add Product
                        </Button>
                      </InlineStack>
                    </InlineStack>

                    {linesLoading ? (
                      <Spinner size="small" />
                    ) : lines.length === 0 ? (
                      <Banner action={canEditSelectedQuotation ? { content: 'Build it from a text', icon: MagicIcon, onAction: () => setShowTextImport(true) } : undefined}>
                        No products added yet. Click "+ Add Product" to start building the quotation, or paste a customer request and let AI build it.
                      </Banner>
                    ) : (
                      <QuotationLineTable
                        lines={lines}
                        quotationId={selectedId}
                        onRefresh={handleLinesRefresh}
                        onLineUpdate={handleLineUpdate}
                        onLineRemove={handleLineRemove}
                        onConfigsRefresh={loadQuotationDetails}
                        onAddToGroup={handleAddToGroup}
                        onReplaceProduct={setReplaceTarget}
                        isLocked={isLocked}
                        areaConfigs={selectedQuotation?.AreaConfigs}
                      />
                    )}
                  </BlockStack>
                </div>
              )}

              {/* Technical Descriptions Tab */}
              {contentTab === 1 && (
                <div style={{ marginTop: '16px' }}>
                  {linesLoading ? (
                    <Spinner size="small" />
                  ) : (
                    <TechDescPanel
                      lines={lines}
                      quotation={selectedQuotation}
                      quotationId={selectedId}
                      onRefresh={handleLinesRefresh}
                      onQuotationRefresh={loadQuotationDetails}
                      isLocked={isLocked}
                      areaConfigs={selectedQuotation?.AreaConfigs}
                    />
                  )}
                </div>
              )}

              {/* PDF Tab */}
              {contentTab === 2 && (
                <div style={{ marginTop: '16px' }}>
                  <PdfExportControls quotationId={selectedId} quotation={selectedQuotation} onQuotationRefresh={loadQuotationDetails} />
                </div>
              )}

              {/* Financial + Notes Tab */}
              {contentTab === 3 && (
                <div style={{ marginTop: '16px' }}>
                  <BlockStack gap="400">
                    <TotalsDashboard quotationId={selectedId} refreshKey={refreshKey} areaConfigs={selectedQuotation?.AreaConfigs} />

                    {selectedQuotation && (
                      <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: '12px' }}>
                        <QuotationNotes quotationId={selectedId} notes={selectedQuotation.Notes} onRefresh={handleLinesRefresh} isLocked={isLocked} />
                      </div>
                    )}
                  </BlockStack>
                </div>
              )}
            </>
          ) : (
            <div style={{ marginTop: '16px' }}>
              <Banner>{quotations.length === 0 ? 'No quotation versions yet. Click "New Version" to create the first quotation.' : 'Select a quotation version above.'}</Banner>
            </div>
          )}
        </Card>

        {/* Add / Replace Product Modal */}
        <AddProductModal
          open={showAddModal || !!replaceTarget}
          onClose={() => {
            setShowAddModal(false);
            setReplaceTarget(null);
          }}
          onAdd={handleProductAdded}
          quotationId={selectedId}
          selectedArea={addPreset.area || 'GRE'}
          selectedLine={addPreset.line}
          selectedUnit={addPreset.unit}
          existingLines={lines}
          replaceLine={replaceTarget}
        />

        {/* AI proofread / translate of every text */}
        {selectedId && (
          <LanguageRevisionModal
            open={showLanguageRevision}
            onClose={() => setShowLanguageRevision(false)}
            quotationId={selectedId}
            pdfLanguage={selectedQuotation?.PdfLanguage || 'E'}
            onApplied={handleRevisionApplied}
          />
        )}

        {/* AI: pasted text to quotation lines */}
        {selectedId && (
          <TextToQuotationModal
            open={showTextImport}
            onClose={() => setShowTextImport(false)}
            quotationId={selectedId}
            pdfLanguage={selectedQuotation?.PdfLanguage || 'E'}
            existingLineCount={lines.length}
            hasIntroduction={
              !!String(selectedQuotation?.PdfIntroduction || '')
                .replace(/<[^>]*>|&nbsp;|\[PAGEBREAK\]/g, '')
                .trim()
            }
            onApplied={handleRevisionApplied}
          />
        )}

        {/* Diff/Compare Modal */}
        <QuotationDiffModal open={compareState.open} onClose={() => setCompareState({ open: false, idA: null, idB: null })} quotations={quotations} idA={compareState.idA} idB={compareState.idB} />

        <Modal open={showCustomerInteractions} onClose={() => setShowCustomerInteractions(false)} title="Customer Interactions" large>
          <Modal.Section>
            <BlockStack gap="300">
              <Banner tone="info">Shows external customer portal activity for this project quotation flow.</Banner>
              <EventTimeLine resourceName="PROJECT_QUOTATION" resourceId={projectId} />
            </BlockStack>
          </Modal.Section>
        </Modal>
        {showCustomerInteractions && (
          <style>{`
            .Polaris-Modal-Dialog__Modal {
              max-width: 1200px !important;
              width: min(1200px, 94vw) !important;
            }
          `}</style>
        )}
      </BlockStack>
    </Box>
  );
}

// Small inline component for notes editing
function QuotationNotes({ quotationId, notes, onRefresh, isLocked }) {
  const [value, setValue] = useState(notes || '');
  const [saving, setSaving] = useState(false);
  const isDirty = value !== (notes || '');

  const handleSave = async () => {
    setSaving(true);
    await fetch(`/api/project-quotations/${quotationId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Notes: value })
    });
    setSaving(false);
    onRefresh();
  };

  if (isLocked) {
    return (
      <BlockStack gap="200">
        <Text variant="headingSm" as="h3">
          Notes
        </Text>
        <Text as="p" variant="bodySm" tone={notes ? undefined : 'subdued'}>
          {notes || 'No notes.'}
        </Text>
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="200">
      <InlineStack align="space-between">
        <Text variant="headingSm" as="h3">
          Notes
        </Text>
        {isDirty && (
          <Button size="slim" onClick={handleSave} loading={saving}>
            Save Notes
          </Button>
        )}
      </InlineStack>
      <TextField value={value} onChange={setValue} multiline={3} autoComplete="off" placeholder="Internal notes for this quotation version..." />
    </BlockStack>
  );
}
